import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IUser } from "../../../shared/interfaces/user/IUser";
import { UserService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import { InstitutionService } from "../../institutions/service";
import Select2 from "../../../shared/components/Select2";
import { usePagination } from "../../../shared/hooks/usePagination";
import { IInstitution } from "../../../shared/interfaces/institution/IInstitution";

const userSchema = yup.object().shape({
  id: yup.number().optional(),
  firstName: yup.string().required("validation.required").min(2).max(50).trim(),
  lastName: yup.string().required("validation.required").min(2).max(50).trim(),
  email: yup
    .string()
    .required("validation.required")
    .email("validation.emailInvalid")
    .trim()
    .lowercase(),
  phoneNumber: yup
    .string()
    .required("validation.required")
    .matches(/^[\d\s\-+()]+$/, "validation.phoneInvalid")
    .min(5)
    .max(20),
  roles: yup.array().of(yup.string()).required("validation.required"),
  enabled: yup.boolean().required("validation.required"),
  password: yup.string().when("id", {
    is: (id: string | undefined) => !Boolean(id),
    then: (schema) =>
      schema.required("validation.required").min(8, "validation.passwordMin"),
    otherwise: (schema) => schema.optional(),
  }),
});

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  roles: [],
  enabled: true,
};

interface IProps {
  cancel: () => void;
  selectedFile?: File | null;
}
const PersonalInfo = ({ cancel, selectedFile }: IProps) => {
  console.log("[PersonalInfo] Component mounted/rendered");
  const { t } = useTranslation();
  const { institutionId } = useParams();
  const { userId } = useParams();
  const [roles, setRoles] = useState<string[]>([]);
  const [institutions, setInstitutions] = useState<IInstitution[] | null>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<IUser>({
    resolver: yupResolver(userSchema) as unknown as Resolver<IUser>,
    defaultValues: async () => {
      if (Boolean(userId)) {
        try {
          const response = await UserService.getSingle(Number(userId));
          return response?.data;
        } catch (e) {
          handleError(e);
          return initialValues;
        }
      }
      return { ...initialValues, institutionId };
    },
  });
  const { pagination } = usePagination({
    perPage: 10,
  });
  const onSubmit = async (data: IUser) => {
    const isUpdate = !!data?.id;

    try {
      console.log("[PersonalInfo] Form submit - isUpdate:", isUpdate);
      console.log("[PersonalInfo] Form data:", data);

      if (isUpdate) {
        // Update user data with JSON only
        console.log("[PersonalInfo] Updating user with id:", data?.id);
        await UserService.update(data, data?.id);

        // Upload image separately if a new file was selected
        if (selectedFile) {
          console.log("[PersonalInfo] Uploading image for user:", data?.id);
          const formData = new FormData();
          formData.append('file', selectedFile);
          await UserService.uploadImage(data.id!, formData);
        }
      } else {
        // Create user with FormData (includes file)
        console.log("[PersonalInfo] Creating new user");
        await UserService.create(data, data?.id, selectedFile);
      }

      cancel();
      reset();

      enqueueSnackbar(
        isUpdate
          ? t("toasts.successfullyUpdated", {
            name: data?.firstName,
          })
          : t("toasts.successfullyCreated", {
            name: data?.firstName,
          }),
        {
          variant: "success",
        },
      );
    } catch (error) {
      console.error("[PersonalInfo] Save error:", error);
      handleError(error);
    }
  };
  const roleOptions = roles.map((role) => ({
    value: role,
    label: t(`role.${role}`),
  }));

  useEffect(() => {
    UserService.getRoles()
      .then((response) => {
        setRoles(response?.data?.map((role) => role?.name));
      })
      .catch(handleError);
  }, []);
  useEffect(() => {
    InstitutionService.getAll(pagination)
      .then((response) => {
        setInstitutions(response?.data);
      })
      .catch(handleError);
  }, [pagination]);
  useEffect(() => {
    UserService.getCountries()
      .then((response) => {
        setCountries(response?.data?.map((country) => country?.code));
      })
      .catch(handleError);
  }, []);

  return (
    <Form onSubmit={(e) => {
      console.log("[PersonalInfo] Form submit event triggered");
      handleSubmit(onSubmit)(e);
    }}>
      <Row>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="firstName">
            <Form.Label>{t("general.firstName")}</Form.Label>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={t("general.firstNamePlaceholder")}
                  isInvalid={!!errors.firstName?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstName?.message &&
                t(errors.firstName.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="lastName">
            <Form.Label>{t("general.lastName")}</Form.Label>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={t("general.lastNamePlaceholder")}
                  isInvalid={!!errors.lastName?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastName?.message && t(errors.lastName.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        {!userId && (
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="password">
              <Form.Label>{t("general.password")}</Form.Label>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="password"
                    placeholder={t("general.passwordPlaceholder")}
                    isInvalid={!!errors.password?.message}
                  />
                )}
              />
              <Form.Control.Feedback type="invalid">
                {errors.password?.message &&
                  t(errors.password.message as string)}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        )}
        <Col sm={4}>
          <Select2
            name="roles"
            control={control}
            label={t("general.roles")}
            placeholder={t("general.selectRole")}
            options={roleOptions}
            isMulti
            error={
              errors.roles?.message
                ? t(errors.roles.message as string)
                : undefined
            }
          />
        </Col>

        <Col sm={4}>
          <Form.Group className="mb-3" controlId="address">
            <Form.Label>{t("general.address")}</Form.Label>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={t("general.addressPlaceholder")}
                  isInvalid={!!errors.address?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address?.message && t(errors.address.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="countryCode">
            <Form.Label>{t("general.country")}</Form.Label>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  isInvalid={!!errors.countryCode?.message}
                >
                  <option hidden>{t("general.select")}</option>
                  {countries?.map((country) => (
                    <option value={country} key={country}>
                      {t(`country.${country}`)}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.countryCode?.message &&
                t(errors.countryCode.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="zipCode">
            <Form.Label>{t("general.zip")}</Form.Label>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={t("general.zipPlaceholder")}
                  isInvalid={!!errors.zipCode?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.zipCode?.message && t(errors.zipCode.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="institutionId">
            <Form.Label>{t("institution.institutions")}</Form.Label>
            <Controller
              name="institutionId"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  value={field.value ? String(field.value) : ""} // Convert here
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  } // Convert back to number
                  isInvalid={!!errors.institutionId?.message}
                >
                  <option value="" hidden>
                    {t("general.select")}
                  </option>
                  {institutions?.map((institution) => (
                    <option
                      value={String(institution?.id)}
                      key={institution?.id}
                    >
                      {institution?.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.institutionId?.message &&
                t(errors.institutionId.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="phoneNumber">
            <Form.Label>{t("general.phone")}</Form.Label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="tel"
                  placeholder={t("general.phoneNumberPlaceholder")}
                  isInvalid={!!errors.phoneNumber?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phoneNumber?.message &&
                t(errors.phoneNumber.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="email">
            <Form.Label>{t("general.email")}</Form.Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="email"
                  placeholder={t("general.emailPlaceholder")}
                  isInvalid={!!errors.email?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message && t(errors.email.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={2}>
          <Form.Group className="my-3" controlId="enabled">
            <Controller
              name="enabled"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Form.Check
                  {...field}
                  type="switch"
                  id="enabled-switch"
                  label={t("general.enabled")}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  isInvalid={!!errors.enabled?.message}
                />
              )}
            />

            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.enabled?.message && t(errors.enabled.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label>{t("general.note")}</Form.Label>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  as="textarea"
                  placeholder={t("general.note")}
                  isInvalid={!!errors.note?.message}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.note?.message && t(errors.note.message as string)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <div className="text-end btn-page">
        <Button variant="secondary" type="button" onClick={cancel}>
          {t("button.cancel")}
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting}
          onClick={() => {
            console.log("[PersonalInfo] Save button clicked");
            console.log("[PersonalInfo] Form errors:", errors);
            console.log("[PersonalInfo] Is submitting:", isSubmitting);
          }}
        >
          {isSubmitting ? t("button.submitting") : t("button.save")}
        </Button>
      </div>
    </Form>
  );
};

export default PersonalInfo;
